/* oxlint-disable max-lines */
import { setup } from 'xstate';

import type { StateActions, StateContext, StateEvents, StateGuards, StateInput } from '../../types';

export const machine = setup({
	/* oxlint-disable typescript/no-unsafe-type-assertion */
	types: {
		context: {} as StateContext,
		events: {} as StateEvents,
		input: {} as StateInput,
	},
	actions: {} as StateActions,
	guards: {} as StateGuards,
	/* oxlint-enable typescript/no-unsafe-type-assertion */
}).createMachine({
	id: 'start-a-petition-edit-variant-a',
	initial: 'intro',
	context: ({ input }) => input,
	states: {
		intro: {
			on: {
				submit: [
					{
						description: 'if user chose the ai driven flow',
						target: 'issue',
						guard: 'isAiDraft',
						actions: ['onSubmit', 'persistDraftType'],
					},
					{
						description: 'if user chose the user driven flow',
						target: 'create',
						guard: 'isUserDraft',
						actions: ['onSubmit', 'persistDraftType'],
					},
				],
			},
		},

		// START: `ai_draft` only steps
		issue: {
			on: {
				back: [{ target: 'intro', actions: ['unsetDraftType'] }],
				submit: [
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{ target: 'personal-story', actions: ['onSubmit'] },
				],
				error: { target: 'create', actions: ['onError'] },
			},
		},
		'personal-story': {
			on: {
				back: [{ target: 'issue' }],
				submit: [
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{ target: 'edit', actions: ['onSubmit'] },
				],
				error: { target: 'create', actions: ['onError'] },
			},
		},
		edit: {
			on: {
				back: [
					// -- edit step only fires `submit` events
				],
				submit: [
					// -- non-step action events (banner, media, and form actions)
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{
						target: 'preview',
						guard: { type: 'transitionTo', params: { target: 'preview' } },
						actions: ['onSubmit'],
					},
					{
						description: 'navigate to media step modality',
						target: 'media',
						guard: { type: 'transitionTo', params: { target: 'media' } },
					},
					// -- back event
					{
						target: 'personal-story',
						guard: {
							type: 'transitionTo',
							params: { target: 'personal-story' },
						},
						actions: ['onSubmit'],
					},
					// -- submit event
					{
						target: 'location',
						guard: { type: 'transitionTo', params: { target: 'location' } },
						actions: ['onSubmit'],
					},
					{
						description: 'default submit if no target specified',
						target: 'location',
						actions: ['onSubmit'],
					},
				],
			},
		},
		preview: {
			on: {
				back: [{ target: 'edit' }],
				submit: [
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{
						description: 'default submit if no target specified',
						target: 'location',
					},
				],
			},
		},

		// END: `ai_draft` only steps

		// START: `user_draft` only steps
		create: {
			on: {
				back: [
					{
						target: 'intro',
						guard: 'isUserDraft',
						actions: ['unsetDraftType'],
					},
				],
				submit: [
					// -- create transitions do not need conditional transitions based on draft.type
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{
						target: 'optional-preview',
						guard: {
							type: 'transitionTo',
							params: { target: 'optional-preview' },
						},
						actions: ['onSubmit'],
					},
					{
						target: 'location',
						guard: { type: 'transitionTo', params: { target: 'location' } },
						actions: ['onSubmit'],
					},
					{
						target: 'media',
						guard: { type: 'transitionTo', params: { target: 'media' } },
						actions: ['onSubmit'],
					},
					{ target: 'location', actions: ['onSubmit'] },
				],
			},
		},
		'optional-preview': {
			on: {
				back: [
					{
						description: 'optional-preview only goes back to create',
						target: 'create',
					},
				],
				submit: [
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{
						description: 'TODO: why?',
						target: 'create',
						guard: { type: 'transitionTo', params: { target: 'create' } },
					},
					{ target: 'location' },
				],
			},
		},
		// END: `user_draft` only steps

		// START: common steps
		media: {
			on: {
				back: [
					{
						description: "{ draft: { type: 'user_draft' } }",
						target: 'create',
						guard: 'isUserDraft',
					},
					{
						description: "{ draft: { type: 'ai_draft' } }",
						target: 'edit',
						guard: 'isAiDraft',
					},
				],
				submit: [
					{
						description: "{ draft: { type: 'user_draft' } }",
						target: 'create',
						guard: 'isUserDraft',
						actions: ['onSubmit'],
					},
					{
						description: "{ draft: { type: 'ai_draft' } }",
						target: 'edit',
						guard: 'isAiDraft',
						actions: ['onSubmit'],
					},
				],
			},
		},
		location: {
			on: {
				back: [
					{
						description: "{ draft: { type: 'user_draft' } }",
						target: 'create',
						guard: 'isUserDraft',
					},
					{
						description: "{ draft: { type: 'ai_draft' } }",
						target: 'edit',
						guard: 'isAiDraft',
					},
				],
				submit: [
					// -- location submit transitions do not need conditional transitions based on draft.type
					{
						target: 'prepublish',
						actions: ['onSubmit'],
					},
				],
			},
		},
		prepublish: {
			on: {
				back: { target: 'location' },
			},
		},
		// END: common steps

		// TODO: move to their own machine
		'chat-app-entry': {
			on: {
				back: [{ target: 'issue' }],
				submit: [
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{ target: 'preview', actions: ['onSubmit', 'persistDraftType'] },
				],
				error: { target: 'create', actions: ['onError'] },
			},
		},
		social: {
			on: {
				submit: [
					{
						description: 'stay on step, update context',
						guard: { type: 'transitionTo', params: { target: 'self' } },
						actions: ['onSubmit'],
					},
					{ target: 'preview', actions: ['onSubmit', 'persistDraftType'] },
				],
				error: { target: 'create', actions: ['onError'] },
			},
		},
	},
});
